import React, { useMemo } from 'react';
import {
  PlusCircle, LayoutDashboard, AlertCircle,
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { isOverdue, getTodayRange, getThisWeekRange, getThisMonthRange } from '../utils/helpers';
import { StatusBadge, PriorityBadge } from './Shared';

export default function Dashboard({ tasks, navigateTo, currentUser, stats }) {
  const todayStats = useMemo(() => {
    const [s, e] = getTodayRange();
    const completedToday = tasks.filter(t => t.status === 'completed' && new Date(t.createdAt) >= s && new Date(t.createdAt) <= e).length;
    return { completedToday };
  }, [tasks]);

  const statusChartData = useMemo(() => [
    { name: '待处理', value: stats.pending, color: '#64748b' },
    { name: '处理中', value: stats.processing, color: '#3b82f6' },
    { name: '待审核', value: stats.review, color: '#f59e0b' },
    { name: '已完成', value: stats.completed, color: '#10b981' },
    { name: '已驳回', value: stats.rejected, color: '#f43f5e' },
    { name: '已归档', value: stats.archived, color: '#9ca3af' },
    { name: '已超时', value: stats.overdue, color: '#f97316' },
  ], [stats]);

  const typeChartData = useMemo(() => {
    const map = {};
    tasks.forEach(t => { map[t.type] = (map[t.type] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [tasks]);

  const priorityChartData = useMemo(() => [
    { name: '紧急', value: tasks.filter(t => t.priority === 'urgent').length, color: '#f43f5e' },
    { name: '高', value: tasks.filter(t => t.priority === 'high').length, color: '#f97316' },
    { name: '中', value: tasks.filter(t => t.priority === 'medium').length, color: '#3b82f6' },
    { name: '低', value: tasks.filter(t => t.priority === 'low').length, color: '#9ca3af' },
  ], [tasks]);

  const weeklyTrend = useMemo(() => {
    const days = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
    const [weekStart] = getThisWeekRange();
    return days.map((day, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      const dateStr = d.toISOString().slice(0, 10);
      const completed = tasks.filter(t => t.status === 'completed' && t.createdAt && t.createdAt.startsWith(dateStr)).length;
      const created = tasks.filter(t => t.createdAt && t.createdAt.startsWith(dateStr)).length;
      return { day, completed, created };
    });
  }, [tasks]);

  const statCards = [
    { label: '全部任务', value: stats.total, color: 'text-slate-700', onClick: () => navigateTo('all') },
    { label: '待处理', value: stats.pending, color: 'text-slate-600', onClick: () => navigateTo('all', { status: 'pending' }) },
    { label: '处理中', value: stats.processing, color: 'text-blue-600', onClick: () => navigateTo('all', { status: 'processing' }) },
    { label: '待审核', value: stats.review, color: 'text-amber-600', onClick: () => navigateTo('leo') },
    { label: '已完成', value: stats.completed, color: 'text-emerald-600', onClick: () => navigateTo('all', { status: 'completed' }) },
    { label: '已驳回', value: stats.rejected, color: 'text-rose-600', onClick: () => navigateTo('all', { status: 'rejected' }) },
    { label: '已超时', value: stats.overdue, color: 'text-orange-600', onClick: () => navigateTo('all', { status: 'overdue' }) },
    { label: '高风险', value: stats.highRisk, color: 'text-rose-600', onClick: () => navigateTo('all', { riskLevel: 'high' }) },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-800">任务中心概览</h1>
        <button onClick={() => navigateTo('create')} className="btn-primary">
          <PlusCircle className="w-4 h-4 mr-2" /> 新建任务
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map(card => (
          <button key={card.label} onClick={card.onClick} className="bg-white border border-slate-200 rounded-xl p-5 text-left hover:shadow-md transition-shadow">
            <div className="text-sm text-slate-500">{card.label}</div>
            <div className={`text-3xl font-bold mt-2 ${card.color}`}>{card.value}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">任务状态分布</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={statusChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {statusChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">任务优先级分布</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={priorityChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#6366f1" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">本周任务趋势</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={weeklyTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="created" fill="#6366f1" name="新建" />
              <Bar dataKey="completed" fill="#10b981" name="完成" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">任务类型分布</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={typeChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {typeChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={`hsl(${index * 45}, 70%, 60%)`} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentTasks tasks={tasks} navigateTo={navigateTo} />
        <PriorityTasks tasks={tasks} navigateTo={navigateTo} />
      </div>
    </div>
  );
}

function RecentTasks({ tasks, navigateTo }) {
  const recent = [...tasks].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5);
  return (
    <div className="card p-5">
      <h3 className="text-sm font-semibold text-slate-700 mb-4">最近创建的任务</h3>
      <div className="space-y-3">
        {recent.map(t => (
          <div key={t.id} onClick={() => navigateTo('detail', t.id)} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-100">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">{t.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">{t.type} · {t.store} · {t.site}</div>
            </div>
            <StatusBadge status={t.status} />
          </div>
        ))}
      </div>
    </div>
  );
}

function PriorityTasks({ tasks, navigateTo }) {
  const urgent = tasks.filter(t => t.priority === 'urgent' && !['completed', 'archived'].includes(t.status)).slice(0, 5);
  return (
    <div className="card p-5">
      <h3 className="text-sm font-semibold text-slate-700 mb-4">紧急任务</h3>
      <div className="space-y-3">
        {urgent.length === 0 && <div className="text-sm text-slate-400">暂无紧急任务</div>}
        {urgent.map(t => (
          <div key={t.id} onClick={() => navigateTo('detail', t.id)} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-100">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">{t.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">截止 {formatDate(t.dueDate)} · {t.assignee}</div>
            </div>
            {isOverdue(t.dueDate, t.status) && <span className="text-xs text-rose-600 font-medium shrink-0 ml-2">已超时</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function formatDate(d) {
  if (!d) return '-';
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
